import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PublicChrome from './components/PublicChrome';
import {
  compositionSkillIds,
  outcomeJsonLd,
  outcomePageDescription,
  outcomePageTitle,
  outcomePath,
  playbookText,
  requiredContext,
  scoreBadgeColor,
  testRigFacts,
  type PublicOutcomeSummary,
} from './outcomeShowcase';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
}

function upsertJsonLd(data: Record<string, unknown>) {
  const id = 'outcome-jsonld';
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function OutcomePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState<PublicOutcomeSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setOutcome(null);
    setError(null);
    fetch(`/api/outcomes/public/${encodeURIComponent(id)}`)
      .then((res) => {
        if (res.status === 404) throw new Error('not-found');
        if (!res.ok) throw new Error('failed');
        return res.json();
      })
      .then((data) => {
        if (!cancelled) setOutcome(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error && err.message === 'not-found' ? 'not-found' : 'failed');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!outcome) return;
    const path = outcomePath(outcome);
    if (id && outcome.slug && id === outcome.id && id !== outcome.slug) {
      navigate(path, { replace: true });
    }
    const canonical = `${window.location.origin}${path}`;
    document.title = outcomePageTitle(outcome);
    upsertMeta('name', 'description', outcomePageDescription(outcome));
    upsertMeta('property', 'og:title', outcomePageTitle(outcome));
    upsertMeta('property', 'og:description', outcomePageDescription(outcome));
    upsertMeta('property', 'og:type', 'article');
    upsertMeta('property', 'og:url', canonical);
    upsertCanonical(canonical);
    upsertJsonLd(outcomeJsonLd(outcome, canonical));
    window.scrollTo(0, 0);
    return () => {
      document.title = 'Sourcetape — quality harness for agent skills';
    };
  }, [outcome, id, navigate]);

  if (error === 'not-found') {
    return (
      <PublicChrome>
        <main className="max-w-3xl mx-auto px-5 py-24 text-center">
          <h1 className="text-2xl font-semibold">This outcome is not public</h1>
          <p className="text-texts mt-2 text-sm">It may still be training, or it never reached a certified score.</p>
          <Link to="/#showcase" className="inline-block mt-6 text-sm text-accent hover:underline">
            Browse certified outcomes
          </Link>
        </main>
      </PublicChrome>
    );
  }

  if (error) {
    return (
      <PublicChrome>
        <main className="max-w-3xl mx-auto px-5 py-24 text-center">
          <h1 className="text-2xl font-semibold">Could not load this outcome</h1>
          <p className="text-texts mt-2 text-sm">The registry did not respond. Try again from the home page.</p>
        </main>
      </PublicChrome>
    );
  }

  if (!outcome) {
    return (
      <PublicChrome>
        <main className="max-w-3xl mx-auto px-5 py-24 text-center text-texts text-sm">Loading outcome...</main>
      </PublicChrome>
    );
  }

  const scoreVal = outcome.score?.overall ?? outcome.quality_score ?? 0;
  const badgeTone = scoreBadgeColor(Number(scoreVal));
  const context = requiredContext(outcome);
  const rig = testRigFacts(outcome);
  const skills = compositionSkillIds(outcome);

  return (
    <PublicChrome>
      <main className="max-w-3xl mx-auto px-5 pt-12 pb-24" data-testid="outcome-page">
        <p className="text-xs uppercase tracking-[0.2em] text-accent mb-4">Certified outcome</p>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-surface text-texts border border-border">
            {outcome.discipline || 'General'}
          </span>
          <span
            className={`text-[11px] font-mono font-bold px-2 py-0.5 rounded ${
              badgeTone === 'emerald'
                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                : badgeTone === 'amber'
                  ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                  : 'bg-slate-500/10 text-slate-400 border border-slate-500/20'
            }`}
          >
            {Number(scoreVal).toFixed(1)}/12
          </span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{outcome.title}</h1>
        <p className="mt-4 text-[15px] text-texts leading-relaxed">
          {outcome.plain_summary || outcome.description || outcome.playbook?.overview || 'No description recorded.'}
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to={`/studio?fork=${outcome.id}`}
            className="text-sm bg-accent hover:bg-accent-hover text-accent-text px-4 py-2 rounded-md font-semibold inline-flex items-center gap-1.5"
          >
            <i className="ri-git-branch-line" /> Fork to Studio
          </Link>
          <Link to="/#showcase" className="text-sm px-4 py-2 rounded-md border border-border text-texts hover:text-textp">
            All outcomes
          </Link>
        </div>

        <section className="mt-14 space-y-3">
          <h2 className="text-lg font-medium tracking-tight">What you need to provide</h2>
          <p className="text-sm text-texts">
            Unique context this pipeline expects. Fork it, then attach these files or payloads before you run.
          </p>
          {context.length ? (
            <ul className="space-y-2">
              {context.map((c) => (
                <li key={`${c.kind}-${c.name}`} className="bg-surface border border-border rounded-lg p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-sm text-accent">{c.name}</span>
                    <span className="text-[10px] uppercase tracking-wider text-texts font-mono">
                      {c.kind}
                      {c.required ? ' · required' : ''}
                    </span>
                  </div>
                  {c.description ? <p className="text-sm text-texts mt-1">{c.description}</p> : null}
                  {c.example ? (
                    <p className="text-[11px] font-mono text-texts mt-1">Example: {c.example}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-texts italic">No unique context was recorded for this outcome.</p>
          )}
        </section>

        <section className="mt-14 space-y-3">
          <h2 className="text-lg font-medium tracking-tight">How it was tested</h2>
          <p className="text-sm text-texts">
            The training harness ran this pipeline against fixtures, scored the artifact against the rubric, and
            published only after the score cleared 10/12.
          </p>
          {rig.length ? (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {rig.map((fact) => (
                <div key={fact.label} className="bg-surface border border-border rounded-lg p-3">
                  <dt className="text-[10px] uppercase tracking-wider text-texts">{fact.label}</dt>
                  <dd className="font-mono text-sm text-textp mt-0.5 break-all">{fact.value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-sm text-texts italic">No training run is linked on this record yet.</p>
          )}
        </section>

        <section className="mt-14 space-y-3">
          <h2 className="text-lg font-medium tracking-tight">Contract</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xs uppercase tracking-wider text-texts mb-2">Inputs</h3>
              <ul className="space-y-1.5">
                {(outcome.contract?.inputs ?? []).map((inp) => (
                  <li key={inp.name} className="bg-surface border border-border rounded p-2 flex justify-between text-sm">
                    <span className="font-mono text-accent">{inp.name}</span>
                    <span className="font-mono text-texts">{inp.type}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-xs uppercase tracking-wider text-texts mb-2">Outputs</h3>
              <ul className="space-y-1.5">
                {(outcome.contract?.outputs ?? []).map((out) => (
                  <li key={out.name} className="bg-surface border border-border rounded p-2 flex justify-between text-sm">
                    <span className="font-mono text-accent">{out.name}</span>
                    <span className="font-mono text-texts">{out.type}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {outcome.contract?.rubric?.length ? (
            <div className="mt-4">
              <h3 className="text-xs uppercase tracking-wider text-texts mb-2">Rubric</h3>
              <ul className="space-y-1.5">
                {outcome.contract.rubric.map((r) => (
                  <li key={r.id} className="bg-surface border border-border rounded p-2 flex justify-between text-sm">
                    <span>{r.name}</span>
                    <span className="font-mono text-emerald-400">{r.max_points} pts</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>

        <section className="mt-14 space-y-3">
          <h2 className="text-lg font-medium tracking-tight">Composition</h2>
          {skills.length ? (
            <ol className="space-y-2">
              {skills.map((skillId, index) => (
                <li key={`${skillId}-${index}`} className="bg-surface border border-border rounded-lg p-3 flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-accent/20 text-accent flex items-center justify-center font-mono text-[11px]">
                    {index + 1}
                  </span>
                  <span className="font-mono text-sm">{skillId}</span>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-sm text-texts italic">No pipeline steps recorded.</p>
          )}
        </section>

        <section className="mt-14 space-y-3">
          <h2 className="text-lg font-medium tracking-tight">Playbook</h2>
          <pre className="bg-surface border border-border rounded-lg p-4 text-[12px] font-mono whitespace-pre-wrap leading-relaxed">
            {playbookText(outcome) || 'No playbook rules were curated for this composition yet.'}
          </pre>
        </section>

        {outcome.score?.checks?.length ? (
          <section className="mt-14 space-y-3">
            <h2 className="text-lg font-medium tracking-tight">Rubric results</h2>
            <ul className="space-y-2">
              {outcome.score.checks.map((chk) => (
                <li key={chk.criterion_id} className="bg-surface border border-border rounded-lg p-3 flex justify-between gap-3">
                  <div>
                    <div className="font-mono text-sm">{chk.criterion_id}</div>
                    <div className="text-xs text-texts mt-0.5">{chk.reason}</div>
                  </div>
                  <span className="font-mono text-emerald-400 shrink-0">+{chk.score.toFixed(1)}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </main>
    </PublicChrome>
  );
}
